import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { RegionProvider } from "@/context/RegionContext";
import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProtectedRoute from "@/components/routing/ProtectedRoute";
import Home from "@/pages/Home";
import About from "@/pages/About";
import { ServicesList, ServiceDetail } from "@/pages/Services";
import { IndustriesList, IndustryDetail } from "@/pages/Industries";
import { CaseStudiesList, CaseStudyDetail } from "@/pages/CaseStudies";
import { InsightsList, InsightDetail } from "@/pages/Insights";
import DiscussProject from "@/pages/DiscussProject";
import Practitioners from "@/pages/Practitioners";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/Auth";
import { ToolboxIndex, ToolboxModule, ToolboxLesson } from "@/pages/Toolbox";
import { MemberHub, MemberResources, MemberResourceDetail, MemberBookmarks, MemberAccount } from "@/pages/Member";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Podcast from "@/pages/Podcast";
import PodcastEpisode from "@/pages/PodcastEpisode";
import Verify from "@/pages/Verify";
import { ShopIndex, ShopProduct, ShopSuccess } from "@/pages/Shop";
import EnterpriseInquiry from "@/pages/EnterpriseInquiry";
import AdminOrders from "@/pages/AdminOrders";
import AdminMembers from "@/pages/AdminMembers";
import AdminCurriculum from "@/pages/AdminCurriculum";
import AdminInsights from "@/pages/AdminInsights";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <RegionProvider>
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <Header />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<ServicesList />} />
                <Route path="/services/:slug" element={<ServiceDetail />} />
                <Route path="/industries" element={<IndustriesList />} />
                <Route path="/industries/:slug" element={<IndustryDetail />} />
                <Route path="/case-studies" element={<CaseStudiesList />} />
                <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
                <Route path="/insights" element={<InsightsList />} />
                <Route path="/insights/:slug" element={<InsightDetail />} />
                <Route path="/contact" element={<DiscussProject />} />
                <Route path="/discuss-a-project" element={<Navigate to="/contact" replace />} />
                <Route path="/practitioners" element={<Practitioners />} />
                <Route path="/podcast" element={<Podcast />} />
                <Route path="/podcast/:slug" element={<PodcastEpisode />} />
                <Route path="/verify/:certId" element={<Verify />} />
                <Route path="/shop" element={<ShopIndex />} />
                <Route path="/shop/success" element={<ShopSuccess />} />
                <Route path="/shop/:slug" element={<ShopProduct />} />
                <Route path="/enterprise-inquiry" element={<EnterpriseInquiry />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/member/hub" element={<ProtectedRoute><MemberHub /></ProtectedRoute>} />
                <Route path="/member/toolbox" element={<ProtectedRoute><ToolboxIndex /></ProtectedRoute>} />
                <Route path="/member/toolbox/:moduleSlug" element={<ProtectedRoute><ToolboxModule /></ProtectedRoute>} />
                <Route path="/member/toolbox/:moduleSlug/:lessonSlug" element={<ProtectedRoute><ToolboxLesson /></ProtectedRoute>} />
                <Route path="/member/resources" element={<ProtectedRoute><MemberResources /></ProtectedRoute>} />
                <Route path="/member/resources/:slug" element={<ProtectedRoute><MemberResourceDetail /></ProtectedRoute>} />
                <Route path="/member/bookmarks" element={<ProtectedRoute><MemberBookmarks /></ProtectedRoute>} />
                <Route path="/member/account" element={<ProtectedRoute><MemberAccount /></ProtectedRoute>} />
                <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
                <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
                <Route path="/admin/orders" element={<ProtectedRoute><AdminOrders /></ProtectedRoute>} />
                <Route path="/admin/members" element={<ProtectedRoute><AdminMembers /></ProtectedRoute>} />
                <Route path="/admin/curriculum" element={<ProtectedRoute><AdminCurriculum /></ProtectedRoute>} />
                <Route path="/admin/insights" element={<ProtectedRoute><AdminInsights /></ProtectedRoute>} />
              </Routes>
            </main>
            <Footer />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </RegionProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
